import type { SupplierPolicy } from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useReducer } from 'react'

import { extractErrorMessage } from '@/api/extract-error'
import { useDirtyVsInitial } from '@/domains/companies'

import { updateSupplierPolicy } from './api'
import { invalidateOnboarding } from './invalidate-onboarding'
import {
  type PoliciesFormAction,
  type PoliciesFormState,
  type PolicyKey,
  PoliciesFormContext
} from './policies-context'

const EMPTY_POLICY: SupplierPolicy = {
  hsePolicy: false,
  codeOfEthicsPolicy: false,
  socialLaborPolicies: false,
  disasterManagementPlan: false,
  licensedExporter: false
}

function reducer(
  state: PoliciesFormState,
  action: PoliciesFormAction
): PoliciesFormState {
  switch (action.type) {
    case 'toggle':
      return {
        ...state,
        [action.field]: !state[action.field],
        noneSelected: false
      }
    case 'selectNone':
      return {
        ...state,
        ...EMPTY_POLICY,
        licensedExporter: state.licensedExporter,
        noneSelected: true
      }
    case 'clearNone':
      return { ...state, noneSelected: false }
    case 'setLicensedExporter':
      return { ...state, licensedExporter: action.value }
    case 'submitError':
      return { ...state, submitError: action.message }
    default:
      return state
  }
}

function initialState(initial: SupplierPolicy | null): PoliciesFormState {
  const policy = initial ?? EMPTY_POLICY
  const noneSelected =
    initial !== null &&
    !policy.hsePolicy &&
    !policy.codeOfEthicsPolicy &&
    !policy.socialLaborPolicies &&
    !policy.disasterManagementPlan
  return { ...policy, noneSelected, submitError: null }
}

const POLICY_KEYS: ReadonlyArray<PolicyKey> = [
  'hsePolicy',
  'codeOfEthicsPolicy',
  'socialLaborPolicies',
  'disasterManagementPlan'
]

export function PoliciesFormProvider({
  children,
  initial,
  onSuccess
}: {
  children: ReactNode
  initial: SupplierPolicy | null
  onSuccess: () => void
}) {
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(reducer, initialState(initial))
  /**
   * register dirty form. noneSelected is a UI toggle, submitError is
   * server feedback. neither counts as user content for the gate.
   */
  useDirtyVsInitial('policies-form', state, ['noneSelected', 'submitError'])

  const mutation = useMutation({
    mutationFn: (body: SupplierPolicy) => updateSupplierPolicy(body),
    onSuccess: async () => {
      await invalidateOnboarding(queryClient, 'supplier-policies')
      onSuccess()
    },
    onError: failure =>
      dispatch({ type: 'submitError', message: extractErrorMessage(failure) })
  })

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (mutation.isPending) return
    const selected = POLICY_KEYS.some(key => state[key])
    if (!state.noneSelected && !selected) return
    dispatch({ type: 'submitError', message: null })
    mutation.mutate({
      hsePolicy: state.hsePolicy,
      codeOfEthicsPolicy: state.codeOfEthicsPolicy,
      socialLaborPolicies: state.socialLaborPolicies,
      disasterManagementPlan: state.disasterManagementPlan,
      licensedExporter: state.licensedExporter
    })
  }

  const selectedCount = POLICY_KEYS.reduce(
    (count, key) => (state[key] ? count + 1 : count),
    0
  )
  const canSubmit = state.noneSelected || selectedCount > 0

  return (
    <PoliciesFormContext.Provider
      value={{
        state,
        dispatch,
        isSubmitting: mutation.isPending,
        selectedCount,
        canSubmit,
        handleSubmit
      }}
    >
      {children}
    </PoliciesFormContext.Provider>
  )
}

export const POLICIES_FIELDS: ReadonlyArray<{
  key: PolicyKey
  label: string
  description: string
}> = [
  {
    key: 'hsePolicy',
    label: 'Health, Safety & Environment policy',
    description:
      'A written HSE policy covering employee safety, environmental impact, and incident response.'
  },
  {
    key: 'codeOfEthicsPolicy',
    label: 'Code of ethics',
    description:
      'A documented code of ethical conduct for employees, suppliers, and partners.'
  },
  {
    key: 'socialLaborPolicies',
    label: 'Social & labor policies',
    description:
      'Policies on fair labor practices, working conditions, and human rights.'
  },
  {
    key: 'disasterManagementPlan',
    label: 'Disaster management plan',
    description:
      'A continuity plan covering natural disasters, supply disruption, and recovery procedures.'
  }
]
